import * as React from "react";
import { Form, useFormApi } from "informed";
import DocumentTitle from "../../misc/components/DocumentTitle";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link, useParams } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SiteFormBody, SiteFormModel } from "./SiteFormBody";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
    return (
        <div className="h2 pull-right">
            <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                Save
            </button>&nbsp;
            <Link className="btn btn-primary btn-outline" to="/Sites" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                Return to List
            </Link>
        </div>
    );
}

export interface EditSiteFormProps {
    site: SiteFormModel;
}

export const EditSiteForm = ({ site }: EditSiteFormProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const formApi = useFormApi<SiteFormModel>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);

    const Client = useWebPortalApiClient();

    const onPreAuditSubmit = async () => {
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            setAuditModalVisible(false);
            return setErrorAlert("Errors exist on the page");
        }

        const apiForm = {
            ...state.values,
        }

        try {
            const siteResult = await Client.client.patchSites({
                studyId: study.id,
                name: apiForm.Name,
                number: apiForm.SiteId,
                address: apiForm.Address,
                country: apiForm.Country,
                timeZone: apiForm.TimeZone,
                contact: apiForm.Contact,
                email: apiForm.Email,
                phone: apiForm.Phone,
            }, site.Id, auditForm.Annotation);

            if (siteResult) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setAuditModalVisible(false);
        setSubmitting(false);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/Sites`}>Sites list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Edit Site</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={onPreAuditSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Details</h4>
                        </div>
                        <div className="panel-body">
                            <SiteFormBody />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onPreAuditSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={async (audit) => await onSubmit(audit)}
                />
            ) : null}
        </>
    );
};

export const EditSiteScreen = () => {
    const { id } = useParams<{ id: string }>();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const client = useWebPortalApiClient();
    const [site, setSite] = React.useState<SiteFormModel>();

    React.useEffect(() => {
        const getData = async () => {
            const siteResult = await client.client.getSite(id);

            const formSite: SiteFormModel = {
                Id: siteResult.id,
                SiteId: siteResult.number,
                Name: siteResult.name,
                Address: siteResult.address,
                Contact: siteResult.contact,
                Country: siteResult.country,
                TimeZone: siteResult.timeZone,
                Email: siteResult.email,
                Phone: siteResult.phone,
            }

            setSite(formSite);
        }

        getData();
    }, [id]);

    if (!site) return (<LoadingWithMessage message="Loading Site..." />);

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.EditSite]}>
            <DocumentTitle title="Edit Site | Vitalograph Web Portal">
                <Form noValidate initialValues={site}>
                    <EditSiteForm site={site} />
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
